'use client'

import React from 'react'
import type { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'
import RichText from '@/components/RichText'
import { cn } from '@/utilities/ui'
import { CTAButton } from '@/components/ui/cta-button'
import { resolveHref } from '@/utilities/resolveHref'
import { mapLegacyAppearance, type LinkFieldType } from '@/fields/link'

type TextBackground = 'white' | 'black' | 'beige' | 'orange' | 'ai' | 'public'

interface TextContentProps {
  richText: DefaultTypedEditorState
  textBackground?: TextBackground
  enableCta?: boolean
  cta?: LinkFieldType | null
  /** @deprecated Use cta.label instead - kept for backwards compatibility with existing content */
  ctaLabel?: string | null
  className?: string
}

const backgroundStyles: Record<TextBackground, string> = {
  white: 'bg-white border border-civo-cloud text-civo-black',
  black: 'bg-civo-black text-white',
  beige: 'bg-civo-cloud text-civo-black',
  orange: 'bg-civo-orange text-civo-black',
  ai: 'bg-civo-ai text-civo-black',
  public: 'bg-civo-public text-civo-black',
}

export const TextContent: React.FC<TextContentProps> = ({
  richText,
  textBackground = 'white',
  enableCta,
  cta,
  ctaLabel,
  className,
}) => {
  const href = cta ? resolveHref(cta) : null
  // Support both new cta.label and legacy ctaLabel for backwards compatibility
  const buttonLabel = cta?.label || ctaLabel
  const hasCTA = enableCta && buttonLabel && href

  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-[16px] p-7',
        backgroundStyles[textBackground],
        className,
      )}
    >
      <RichText
        data={richText}
        enableGutter={false}
        enableProse={false}
        className="w-full"
      />
      {hasCTA && (
        <div className="mt-auto pt-4 flex">
          <CTAButton
            variant={mapLegacyAppearance(cta?.appearance)}
            href={href}
            newTab={cta?.newTab ?? false}
            surface={textBackground === 'black' ? 'black' : undefined}
          >
            {buttonLabel}
          </CTAButton>
        </div>
      )}
    </div>
  )
}